import React, { useEffect, useMemo, useState } from 'react';
import { canAccess } from '../utils/canAccess';
import { getDefaultDates } from '../utils/dateUtils';
import { defaultSelectStyles as selectStyles } from '../utils/selectStyles';
import axios from 'axios';
import { Modal, Button, Form, Card, Row, Col, Badge, Alert, Spinner } from 'react-bootstrap';
import ConfirmModal from '../components/ConfirmModal';
import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory from 'react-bootstrap-table2-paginator';
import filterFactory, { textFilter } from 'react-bootstrap-table2-filter';
import Select from 'react-select';
import * as XLSX from 'xlsx';
import {
  FaCalendarAlt,
  FaTools,
  FaHardHat,
  FaIdCard,
  FaGraduationCap,
  FaBuilding,
  FaClock,
  FaRegCalendarCheck,
  FaUser,
  FaStickyNote,
  FaMoneyBillWave,
  FaEdit,
  FaTrashAlt,
  FaPlus,
  FaFileExport,
  FaFilter,
  FaBroom,
  FaLayerGroup,
  FaCheckCircle,
  FaCommentDots
} from 'react-icons/fa';
import KpiCard from '../components/KpiCard';

const PointagePage = ({ user }) => {
  const { startDate: defaultStartDate, endDate: defaultEndDate } = getDefaultDates();

  const [pointages, setPointages] = useState([]);
  const [ouvriers, setOuvriers] = useState([]);
  const [chantiers, setChantiers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [startDate, setStartDate] = useState(defaultStartDate);
  const [endDate, setEndDate] = useState(defaultEndDate);

  const [selectedChantiers, setSelectedChantiers] = useState([]);
  const [selectedOuvriers, setSelectedOuvriers] = useState([]);

  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedPointage, setSelectedPointage] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const [kpiFilter, setKpiFilter] = useState('ALL');

  const [alertMsg, setAlertMsg] = useState({ type: '', text: '' });

  const [formData, setFormData] = useState({
    ouvrier_id: '',
    chantier_id: '',
    jours: '',
    heures: '',
    date: '',
    observation: ''
  });

  const isAdmin       = user?.role !== 'pointeur' && canAccess(user, 'pointages:delete');
  const isAdminOrResp = user?.role !== 'pointeur' && canAccess(user, 'pointages:write');
  const canSeeSalary  = canAccess(user, 'pointages:all');

  const showAlert = (type, text) => {
    setAlertMsg({ type, text });
    if (text) {
      setTimeout(() => {
        setAlertMsg({ type: '', text: '' });
      }, 3000);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);

      let url = '/pointages';
      if (startDate && endDate) {
        url = `/pointages?start=${startDate}&end=${endDate}`;
      }

      const [resPointages, resOuvriers, resChantiers] = await Promise.all([
        axios.get(url),
        axios.get('/ouvriers'),
        axios.get('/chantiers')
      ]);

      setPointages(Array.isArray(resPointages.data) ? resPointages.data : []);
      setOuvriers(Array.isArray(resOuvriers.data) ? resOuvriers.data : []);
      setChantiers(Array.isArray(resChantiers.data) ? resChantiers.data : []);
    } catch (err) {
      console.error(err);
      showAlert('danger', '❌ Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (startDate && endDate) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  const chantierOptions = useMemo(() => {
    const map = new Map();

    (chantiers || []).forEach((c) => {
      if (c?.name) {
        map.set(c.name, { value: c.name, label: c.name });
      }
    });

    (pointages || []).forEach((p) => {
      if (p?.chantier) {
        map.set(p.chantier, { value: p.chantier, label: p.chantier });
      }
    });

    return [...map.values()].sort((a, b) =>
      a.label.localeCompare(b.label, 'fr', { sensitivity: 'base' })
    );
  }, [chantiers, pointages]);

  const ouvrierOptions = useMemo(() => {
    const map = new Map();

    (ouvriers || []).forEach((o) => {
      if (o?.id) {
        map.set(o.id, {
          value: o.id,
          label: `${o.name || 'Sans nom'}${o.cin ? ` - ${o.cin}` : ''}`
        });
      }
    });

    (pointages || []).forEach((p) => {
      if (p?.ouvrier?.id) {
        map.set(p.ouvrier.id, {
          value: p.ouvrier.id,
          label: `${p.ouvrier.name || 'Sans nom'}${p.ouvrier.cin ? ` - ${p.ouvrier.cin}` : ''}`
        });
      }
    });

    return [...map.values()].sort((a, b) =>
      a.label.localeCompare(b.label, 'fr', { sensitivity: 'base' })
    );
  }, [ouvriers, pointages]);

  const openModal = () => {
    setFormData({
      ouvrier_id: '',
      chantier_id: '',
      jours: '',
      heures: '',
      date: startDate || '',
      observation: ''
    });
    setShowModal(true);
  };

  const openEditModal = (pointage) => {
    setSelectedPointage(pointage);
    setFormData({
      ouvrier_id: pointage.ouvrier?.id || '',
      chantier_id: chantiers.find((c) => c.name === pointage.chantier)?.id || '',
      jours: pointage.jours ?? '',
      heures: pointage.heures ?? '',
      date: pointage.date || '',
      observation: pointage.observation || ''
    });
    setShowEditModal(true);
  };

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const validateForm = () => {
    const jours = Number(formData.jours || 0);
    const heures = Number(formData.heures || 0);

    if (jours < 0 || heures < 0) {
      showAlert('warning', '❌ Jours et heures doivent être supérieurs ou égaux à 0');
      return false;
    }

    if (jours === 0 && heures === 0) {
      showAlert('warning', '❌ Jours et heures ne peuvent pas être tous les deux à 0');
      return false;
    }

    if (!formData.ouvrier_id || !formData.chantier_id || !formData.date) {
      showAlert('warning', '❌ Merci de renseigner ouvrier, chantier et date');
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      await axios.post('/pointages', formData);
      setShowModal(false);
      showAlert('success', '✅ Pointage ajouté avec succès');
      fetchData();
    } catch (err) {
      console.error(err);
      showAlert('danger', err.response?.data?.message || '❌ Erreur enregistrement');
    }
  };

  const handleUpdate = async () => {
    const jours = Number(formData.jours || 0);
    const heures = Number(formData.heures || 0);

    if (jours < 0 || heures < 0 || (jours === 0 && heures === 0)) {
      showAlert('warning', '❌ Jours et heures invalides');
      return;
    }

    try {
      await axios.put(`/pointages/${selectedPointage.id}`, formData);
      setShowEditModal(false);
      showAlert('success', '✅ Pointage modifié avec succès');
      fetchData();
    } catch (err) {
      console.error(err);
      showAlert('danger', err.response?.data?.message || '❌ Erreur mise à jour');
    }
  };

  const handleDelete = (id) => {
    if (!isAdmin) return;
    setConfirmDeleteId(id);
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`/pointages/${confirmDeleteId}`);
      showAlert('success', '✅ Pointage supprimé');
      fetchData();
    } catch (err) {
      console.error(err);
      showAlert('danger', '❌ Erreur suppression');
    } finally {
      setConfirmDeleteId(null);
    }
  };

  const resetAllFilters = () => {
    setKpiFilter('ALL');
    setSelectedChantiers([]);
    setSelectedOuvriers([]);
  };

  // ✅ Base filtrée UNIQUEMENT par chantier + ouvrier
  const baseFilteredPointages = useMemo(() => {
    let rows = [...pointages];

    if (selectedChantiers.length > 0) {
      const chantierSet = new Set(selectedChantiers.map((x) => x.value));
      rows = rows.filter((p) => chantierSet.has(p.chantier));
    }

    if (selectedOuvriers.length > 0) {
      const ouvrierSet = new Set(selectedOuvriers.map((x) => Number(x.value)));
      rows = rows.filter((p) => ouvrierSet.has(Number(p.ouvrier?.id)));
    }

    return rows;
  }, [pointages, selectedChantiers, selectedOuvriers]);

  const distinctChantiers = useMemo(() => {
    return new Set(baseFilteredPointages.map((p) => p.chantier).filter(Boolean)).size;
  }, [baseFilteredPointages]);

  const distinctOuvriers = useMemo(() => {
    return new Set(baseFilteredPointages.map((p) => p.ouvrier?.id).filter(Boolean)).size;
  }, [baseFilteredPointages]);

  // ✅ KPI recalculés sur la base filtrée
  const kpis = useMemo(() => {
    const total = baseFilteredPointages.length;
    const joursRows = baseFilteredPointages.filter((p) => Number(p.jours || 0) > 0).length;
    const heuresRows = baseFilteredPointages.filter((p) => Number(p.heures || 0) > 0).length;
    const obsRows = baseFilteredPointages.filter((p) => (p.observation || '').trim() !== '').length;

    const totalJours = baseFilteredPointages.reduce((sum, p) => sum + Number(p.jours || 0), 0);
    const totalHeures = baseFilteredPointages.reduce((sum, p) => sum + Number(p.heures || 0), 0);

    const masseSalariale = baseFilteredPointages.reduce((sum, p) => {
      const salaire = Number(p.ouvrier?.salary || p.salaire || 0);
      const jours = Number(p.jours || 0);
      const heures = Number(p.heures || 0);
      return sum + (jours + heures / 10) * salaire;
    }, 0);

    return {
      total,
      joursRows,
      heuresRows,
      obsRows,
      totalJours: Number(totalJours.toFixed(2)),
      totalHeures: Number(totalHeures.toFixed(2)),
      masseSalariale: Number(masseSalariale.toFixed(2))
    };
  }, [baseFilteredPointages]);

  // ✅ Tableau final = base filtrée + filtre KPI
  const filteredPointages = useMemo(() => {
    let rows = [...baseFilteredPointages];

    switch (kpiFilter) {
      case 'JOURS':
        rows = rows.filter((p) => Number(p.jours || 0) > 0);
        break;
      case 'HEURES':
        rows = rows.filter((p) => Number(p.heures || 0) > 0);
        break;
      case 'OBS':
        rows = rows.filter((p) => (p.observation || '').trim() !== '');
        break;
      default:
        break;
    }

    return rows;
  }, [baseFilteredPointages, kpiFilter]);

  const handleExportExcel = () => {
    const exportData = filteredPointages.map((row) => ({
      ID: row.id,
      Ouvrier_ID: row.ouvrier?.id || '',
      Date: row.date,
      Ouvrier: row.ouvrier?.name || '',
      CIN: row.ouvrier?.cin || '',
      Qualification: row.ouvrier?.qualification || '',
      Matricule_Sage: row.ouvrier?.matricule_sage || '',
      Chantier: row.chantier || '',
      Jours: row.jours,
      Heures: row.heures,
      Observation: row.observation || '',
      ...(canSeeSalary && { Salaire: row.ouvrier?.salary || 0 })
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Pointages');

    const filename = `pointages_shems_${startDate}_${endDate}.xlsx`;
    XLSX.writeFile(wb, filename);
  };

  const columns = [
    {
      dataField: 'id',
      text: <><FaUser /> ID Pointage</>,
      sort: true,
      filter: textFilter({ placeholder: '🔎 ID...' })
    },
    {
      dataField: 'ouvrier.id',
      text: <><FaIdCard /> ID Ouvrier</>,
      sort: true,
      formatter: (_, row) => row.ouvrier?.id ?? '—',
      filter: textFilter({ placeholder: '🔎 ID Ouvrier...' })
    },
    {
      dataField: 'date',
      text: <><FaCalendarAlt /> Date</>,
      sort: true,
      filter: textFilter({ placeholder: '🔎 Date...' })
    },
    {
      dataField: 'ouvrier.name',
      text: <><FaHardHat /> Ouvrier</>,
      sort: true,
      formatter: (_, row) => row.ouvrier?.name || '—',
      filter: textFilter({ placeholder: '🔎 Nom...' })
    },
    {
      dataField: 'ouvrier.cin',
      text: <><FaIdCard /> CIN</>,
      sort: true,
      formatter: (_, row) => row.ouvrier?.cin || '',
      filter: textFilter({ placeholder: '🔎 CIN...' })
    },
    {
      dataField: 'ouvrier.qualification',
      text: <><FaGraduationCap /> Qualification</>,
      sort: true,
      formatter: (_, row) => row.ouvrier?.qualification || '',
      filter: textFilter({ placeholder: '🔎 Qualif...' })
    },
    {
      dataField: 'ouvrier.matricule_sage',
      text: <><FaIdCard /> Matricule Sage</>,
      sort: true,
      formatter: (_, row) => row.ouvrier?.matricule_sage || '—',
      filter: textFilter({ placeholder: '🔎 Matricule...' })
    },
    {
      dataField: 'chantier',
      text: <><FaBuilding /> Chantier</>,
      sort: true,
      filter: textFilter({ placeholder: '🔎 Chantier...' })
    },
    {
      dataField: 'jours',
      text: <><FaRegCalendarCheck /> Jours</>,
      sort: true,
      formatter: (cell) => Number(cell || 0)
    },
    {
      dataField: 'heures',
      text: <><FaClock /> Heures</>,
      sort: true,
      formatter: (cell) => Number(cell || 0)
    },
    {
      dataField: 'observation',
      text: <><FaStickyNote /> Observation</>,
      formatter: (cell) => cell || '—',
      filter: textFilter({ placeholder: '🔎 Obs...' })
    }
  ];

  if (canSeeSalary) {
    columns.push({
      dataField: 'ouvrier.salary',
      text: <><FaMoneyBillWave /> Salaire</>,
      formatter: (_, row) => `${row.ouvrier?.salary || 0} DH`
    });
  }

  columns.push({
    dataField: 'actions',
    text: <><FaTools /> Actions</>,
    formatter: (_, row) => (
      <>
        {isAdminOrResp && (
          <Button variant="primary" size="sm" className="me-2" onClick={() => openEditModal(row)}>
            <FaEdit />
          </Button>
        )}
        {isAdmin && (
          <Button variant="danger" size="sm" onClick={() => handleDelete(row.id)}>
            <FaTrashAlt />
          </Button>
        )}
      </>
    ),
    headerAlign: 'center',
    align: 'center',
    style: { width: '100px' }
  });

  const paginationOptions = paginationFactory({
    sizePerPage: 20,
    sizePerPageList: [
      { text: '20', value: 20 },
      { text: '50', value: 50 },
      { text: '100', value: 100 },
      { text: '200', value: 200 },
      { text: 'TOUT', value: filteredPointages.length || 1 }
    ],
    showTotal: true
  });

  return (
    <div className="container mt-4 mb-4">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-3">
        <div>
          <h2 className="d-flex align-items-center gap-2 mb-1" style={{ color: '#6d4e36', fontWeight: 800 }}>
            <FaClock /> Liste des pointages
          </h2>
          <div className="text-muted">
            Suivi détaillé des pointages avec filtres par période, chantier, ouvrier et KPI interactifs.
          </div>
        </div>

        <div className="d-flex gap-2 flex-wrap">
          <Badge bg="light" text="dark" style={{ border: '1px solid #d8c8b8', padding: '10px 12px' }}>
            👤 {user?.login || user?.name || 'Utilisateur'}
          </Badge>
          <Badge bg="dark" style={{ padding: '10px 12px' }}>
            📅 {startDate} → {endDate}
          </Badge>
        </div>
      </div>

      {alertMsg.text ? (
        <Alert variant={alertMsg.type} className="mt-3">
          {alertMsg.text}
        </Alert>
      ) : null}

      <Card
        className="mb-3"
        style={{
          borderRadius: '16px',
          border: '1px solid rgba(109,78,54,0.18)',
          boxShadow: '0 8px 24px rgba(109,78,54,0.08)',
          background: '#fffdf8'
        }}
      >
        <Card.Body>
          <Row className="g-3">
            <Col md={2}>
              <Form.Label className="fw-bold"><FaCalendarAlt className="me-2" />Date Début</Form.Label>
              <Form.Control type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </Col>

            <Col md={2}>
              <Form.Label className="fw-bold"><FaCalendarAlt className="me-2" />Date Fin</Form.Label>
              <Form.Control type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </Col>

            <Col md={4}>
              <Form.Label className="fw-bold"><FaBuilding className="me-2" />Chantiers</Form.Label>
              <Select
                isMulti
                isClearable
                isSearchable
                styles={selectStyles}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                placeholder="Filtrer par chantier..."
                options={chantierOptions}
                value={selectedChantiers}
                onChange={(val) => setSelectedChantiers(val || [])}
              />
            </Col>

            <Col md={4}>
              <Form.Label className="fw-bold"><FaHardHat className="me-2" />Ouvriers</Form.Label>
              <Select
                isMulti
                isClearable
                isSearchable
                styles={selectStyles}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                placeholder="Filtrer par ouvrier..."
                options={ouvrierOptions}
                value={selectedOuvriers}
                onChange={(val) => setSelectedOuvriers(val || [])}
              />
            </Col>

            <Col md={12} className="d-flex align-items-end">
              <div className="d-flex gap-2 flex-wrap w-100">
                {isAdminOrResp && (
                  <Button variant="success" onClick={openModal}>
                    <FaPlus className="me-2" />
                    Ajouter
                  </Button>
                )}

                <Button variant="primary" onClick={handleExportExcel}>
                  <FaFileExport className="me-2" />
                  Export Excel
                </Button>

                <Button variant="outline-secondary" onClick={resetAllFilters}>
                  <FaBroom className="me-2" />
                  Reset filtres
                </Button>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Row className="g-3 mb-3">
        <Col md={3}>
          <KpiCard
            title="Total pointages"
            value={kpis.total}
            sub={`${distinctChantiers} chantier(s) • ${distinctOuvriers} ouvrier(s)`}
            color="#6d4e36"
            bg="#f8f2ea"
            icon={FaLayerGroup}
            onClick={() => setKpiFilter('ALL')}
            active={kpiFilter === 'ALL'}
          />
        </Col>

        <Col md={3}>
          <KpiCard
            title="Avec jours"
            value={kpis.joursRows}
            sub={`Total jours : ${kpis.totalJours}`}
            color="#0d6efd"
            bg="#eef7ff"
            icon={FaRegCalendarCheck}
            onClick={() => setKpiFilter('JOURS')}
            active={kpiFilter === 'JOURS'}
          />
        </Col>

        <Col md={3}>
          <KpiCard
            title="Avec heures"
            value={kpis.heuresRows}
            sub={`Total heures : ${kpis.totalHeures}`}
            color="#198754"
            bg="#eefbf3"
            icon={FaCheckCircle}
            onClick={() => setKpiFilter('HEURES')}
            active={kpiFilter === 'HEURES'}
          />
        </Col>

        <Col md={3}>
          <KpiCard
            title="Avec observation"
            value={kpis.obsRows}
            sub="Commentaires saisis"
            color="#d17b00"
            bg="#fff7ea"
            icon={FaCommentDots}
            onClick={() => setKpiFilter('OBS')}
            active={kpiFilter === 'OBS'}
          />
        </Col>
      </Row>

      {canSeeSalary && (
        <Row className="g-3 mb-3">
          <Col md={4}>
            <KpiCard
              title="Masse salariale estimée"
              value={`${kpis.masseSalariale.toLocaleString('fr-FR')} DH`}
              sub="Calcul sur les filtres actifs"
              color="#495057"
              bg="#f7f8fb"
              icon={FaMoneyBillWave}
            />
          </Col>

          <Col md={4}>
            <KpiCard
              title="Chantiers distincts"
              value={distinctChantiers}
              sub="Après filtres chantier / ouvrier"
              color="#6d4e36"
              icon={FaBuilding}
            />
          </Col>

          <Col md={4}>
            <KpiCard
              title="Ouvriers distincts"
              value={distinctOuvriers}
              sub="Après filtres chantier / ouvrier"
              color="#6d4e36"
              icon={FaHardHat}
            />
          </Col>
        </Row>
      )}

      <Card
        className="mb-3"
        style={{
          borderRadius: '16px',
          border: '1px solid rgba(109,78,54,0.18)',
          boxShadow: '0 8px 24px rgba(109,78,54,0.08)'
        }}
      >
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
            <div className="d-flex align-items-center gap-2">
              <FaFilter />
              <strong style={{ color: '#6d4e36' }}>
                KPI actif : {kpiFilter === 'ALL' ? 'Tous les pointages' : kpiFilter}
              </strong>
            </div>

            <div className="d-flex gap-2 flex-wrap">
              {selectedChantiers.length > 0 && (
                <Badge bg="info" text="dark" style={{ padding: '8px 12px' }}>
                  Chantiers : {selectedChantiers.length}
                </Badge>
              )}
              {selectedOuvriers.length > 0 && (
                <Badge bg="warning" text="dark" style={{ padding: '8px 12px' }}>
                  Ouvriers : {selectedOuvriers.length}
                </Badge>
              )}
              <Badge bg="secondary" style={{ padding: '8px 12px' }}>
                Résultats : {filteredPointages.length}
              </Badge>
            </div>
          </div>

          {loading ? (
            <div className="d-flex align-items-center gap-2">
              <Spinner animation="border" size="sm" />
              <span>Chargement...</span>
            </div>
          ) : (
            <BootstrapTable
              keyField="id"
              data={filteredPointages}
              columns={columns}
              pagination={paginationOptions}
              filter={filterFactory()}
              striped
              hover
              condensed
              noDataIndication="Aucun pointage trouvé ❌"
              wrapperClasses="table-responsive"
            />
          )}
        </Card.Body>
      </Card>

      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton style={{ background: '#f8f2ea' }}>
          <Modal.Title>➕ Ajouter un pointage</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Ouvrier</Form.Label>
              <Form.Select name="ouvrier_id" value={formData.ouvrier_id} onChange={handleChange}>
                <option value="">-- Sélectionner --</option>
                {ouvriers.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name} - {o.cin}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Chantier</Form.Label>
              <Form.Select name="chantier_id" value={formData.chantier_id} onChange={handleChange}>
                <option value="">-- Sélectionner --</option>
                {chantiers.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Jours</Form.Label>
              <Form.Control type="number" name="jours" value={formData.jours} onChange={handleChange} />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Heures</Form.Label>
              <Form.Control type="number" name="heures" value={formData.heures} onChange={handleChange} />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Date</Form.Label>
              <Form.Control type="date" name="date" value={formData.date} onChange={handleChange} />
            </Form.Group>

            <Form.Group>
              <Form.Label>Observation</Form.Label>
              <Form.Control as="textarea" rows={2} name="observation" value={formData.observation} onChange={handleChange} />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>Annuler</Button>
          <Button variant="success" onClick={handleSubmit}>Enregistrer</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered>
        <Modal.Header closeButton style={{ background: '#f8f2ea' }}>
          <Modal.Title>✏️ Modifier le pointage</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Chantier</Form.Label>
              <Form.Select name="chantier_id" value={formData.chantier_id} onChange={handleChange}>
                <option value="">-- Sélectionner --</option>
                {chantiers.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Jours</Form.Label>
              <Form.Control type="number" name="jours" value={formData.jours} onChange={handleChange} />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Heures</Form.Label>
              <Form.Control type="number" name="heures" value={formData.heures} onChange={handleChange} />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Date</Form.Label>
              <Form.Control type="date" name="date" value={formData.date} onChange={handleChange} />
            </Form.Group>

            <Form.Group>
              <Form.Label>Observation</Form.Label>
              <Form.Control as="textarea" name="observation" rows={2} value={formData.observation} onChange={handleChange} />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Annuler
          </Button>
          <Button variant="primary" onClick={handleUpdate}>
            Enregistrer
          </Button>
        </Modal.Footer>
      </Modal>

      <ConfirmModal
        show={confirmDeleteId !== null}
        title="Supprimer le pointage"
        message="Êtes-vous sûr de vouloir supprimer ce pointage ?"
        onConfirm={confirmDelete}
        onCancel={() => setConfirmDeleteId(null)}
      />
    </div>
  );
};

export default PointagePage;